import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { AppleTvModels } from '@models/apple-tv.models';
import { AppleTvActions } from '@states/apple-tv/apple-tv.action-types';
import { Dictionary } from '@ngrx/entity/src/models';
import { WallV2Model } from '@models/wall-v2.model';

export interface AppleTvState extends EntityState<AppleTvModels.AppleTvWithWallDocument> {
  isInitialLoaded: boolean;
  isLoading: boolean;
  filters: {
    query: string
  };
  isSaving: boolean;
  isDeleting: boolean;
  selectedAppleTvDocument: AppleTvModels.AppleTvWithWallDocument;
  walls: Dictionary<WallV2Model.WallMongoDocument>;
  isFormValid: boolean;
  deletingEdgeIds: string[];
  configuringEdgeIds: string[];
  deviceWallState: Dictionary<AppleTvModels.AppleTvGetWallFromDeviceState>;
  canDeactivate: boolean;
}

export const adapter: EntityAdapter<AppleTvModels.AppleTvWithWallDocument> = createEntityAdapter<AppleTvModels.AppleTvWithWallDocument>({
  selectId: (document: AppleTvModels.AppleTvWithWallDocument) => document._id,
});


export const initialState: AppleTvState = adapter.getInitialState({
  isInitialLoaded: false,
  isLoading: false,
  filters: {
    query: null,
  },
  isSaving: false,
  isDeleting: false,
  selectedAppleTvDocument: null,
  walls: null,
  isFormValid: false,
  deletingEdgeIds: [],
  configuringEdgeIds: [],
  deviceWallState: {},
  canDeactivate: true,
});

export const appleTvStateReducer = createReducer(
  initialState,
  on(AppleTvActions.resetToInitialState, state => {
    return {
      ...initialState,
    };
  }),
  on(AppleTvActions.getAppleTv, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(AppleTvActions.getAppleTvSuccess, (state, { documents }) => {
    return adapter.setMany(documents, {
      ...state,
      isLoading: false,
      isInitialLoaded: true,
    });
  }),
  on(AppleTvActions.getAppleTvFail, (state) => {
    return {
      ...state,
      isLoading: false,
      isInitialLoaded: true,
    }
  }),
  on(AppleTvActions.setQueryStringFilter, (state, { prop, value }) => {
    return {
      ...state,
      isLoading: true,
      filters: {
        ...state.filters,
        [prop]: value,
      },
    };
  }),
  on(AppleTvActions.resetEntities, (state) => {
    return {
      ...state,
      entities: initialState.entities,
    };
  }),
  on(AppleTvActions.setIsDocumentSaving, (state, { isSaving }) => {
    return {
      ...state,
      isSaving: isSaving,
    };
  }),
  on(AppleTvActions.setCanDeactivate, (state, { canDeactivate }) => {
    return {
      ...state,
      canDeactivate,
    };
  }),
  on(AppleTvActions.setIsDeleting, (state, { isDeleting }) => {
    return {
      ...state,
      isDeleting,
    };
  }),
  on(AppleTvActions.saveAppleTvSetOne, (state, { document }) => {
    return adapter.addOne(document, {
      ...state,
    });
  }),
  on(AppleTvActions.deleteDeviceSuccess, (state, { edgeId }) => {
    const deletingIndex = state.deletingEdgeIds.findIndex(id => id === edgeId);
    let newDeletingArray = [...state.deletingEdgeIds];
    newDeletingArray.splice(deletingIndex, 1);
    return adapter.removeOne(edgeId, {
      ...state,
      deletingEdgeIds: newDeletingArray,
    });
  }),
  on(AppleTvActions.deleteDeviceFail, (state, { edgeId }) => {
    const deletingIndex = state.deletingEdgeIds.findIndex(id => id === edgeId);
    let newDeletingArray = [...state.deletingEdgeIds];
    newDeletingArray.splice(deletingIndex, 1);
    return {
      ...state,
      deletingEdgeIds: newDeletingArray,
    };
  }),
  on(AppleTvActions.getOneAppleTvSuccess, (state, { document }) => {
    return {
      ...state,
      selectedAppleTvDocument: document,
    };
  }),
  on(AppleTvActions.getWallsSuccess, (state, { walls }) => {
    const wallMap = {};
    walls.forEach(wall => {
      wallMap[wall._id] = wall;
    });
    return {
      ...state,
      walls: wallMap,
    };
  }),
  on(AppleTvActions.updateAppleTvSetOne, (state, { response }) => {
    const configureArray = [...state.configuringEdgeIds];
    const index = configureArray.findIndex(id => id === response.edgeId);
    configureArray.splice(index, 1);
    return adapter.updateOne({
      id: response.edgeId,
      changes: {
        wallId: response.wallId,
        wallName: response.wallName,
      },
    }, {
      ...state,
      configuringEdgeIds: configureArray,
    });
  }),
  on(AppleTvActions.changeFormValidation, (state, { isFormValid }) => {
    return {
      ...state,
      isFormValid,
    };
  }),
  on(AppleTvActions.startDeleteDevice, (state, { edgeId }) => {
    return {
      ...state,
      deletingEdgeIds: [...state.deletingEdgeIds, edgeId],
    };
  }),
  on(AppleTvActions.startConfigureAppleTv, (state, { document }) => {
    const deviceWallState = { ...state.deviceWallState };
    delete deviceWallState[document.edgeId];
    return {
      ...state,
      configuringEdgeIds: [...state.configuringEdgeIds, document.edgeId],
      deviceWallState,
    };
  }),
  on(AppleTvActions.getWallFromDeviceSuccess, (state, { edgeId, wall, isEqual }) => {
    const deviceWallState = { ...state.deviceWallState };
    deviceWallState[edgeId] = {
      success: true,
      message: isEqual ? 'Device wall is up to date' : 'Wall in device is not up to date. Click to reconfigure',
      wallNotFound: false,
    };
    return adapter.updateOne({
      id: edgeId,
      changes: {
        isEqual,
        deviceWall: wall,
      },
    }, {
      ...state,
      deviceWallState,
      selectedAppleTvDocument: {
        ...state.selectedAppleTvDocument,
        isEqual: isEqual
      }
    });
  }),
  on(AppleTvActions.rmEdgeIdFromConfigureEdges, (state, { edgeId }) => {
    const configureArray = [...state.configuringEdgeIds];
    const index = configureArray.findIndex(id => id === edgeId);
    configureArray.splice(index, 1);
    return {
      ...state,
      configuringEdgeIds: configureArray,
    };
  }),
  on(AppleTvActions.getWallFromDeviceFail, (state, { edgeId, message, wallNotFound }) => {
    const deviceWallState = { ...state.deviceWallState };
    deviceWallState[edgeId] = {
      success: false,
      message: message,
      wallNotFound,
    };
    return {
      ...state,
      deviceWallState,
    };
  }),
  on(AppleTvActions.getWallFromDevice, (state, { edgeId }) => {
    const deviceWallState = { ...state.deviceWallState };
    delete deviceWallState[edgeId];
    return {
      ...state,
      deviceWallState,
    };
  }),
);
